// Portal switcher for patient-portal users who also hold other roles (e.g.
// ROLE_DOCTOR, ROLE_CLINIC_OWNER, support roles). Pure-patient/consumer users
// (ROLE_USER, ROLE_PATIENT, ROLE_PROFESSIONAL_USER only) never see this.
//
// Uses the same PORTAL_CONFIG + getUserRoles() helpers as the business-portal
// PortalSwitcherButton, but rendered in the patient portal's styled-components
// theme rather than with MUI so it inherits the clinical look-and-feel.

import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import { getUserRoles } from 'src/mentor/layout/utils'
import { PORTAL_CONFIG } from 'src/shared/config/portalConfig'

// Roles that belong exclusively to the patient / consumer experience.
// If the user holds ONLY a subset of these, they don't need a portal switcher.
const PATIENT_CONSUMER_ROLES = ['ROLE_USER', 'ROLE_PATIENT', 'ROLE_PROFESSIONAL_USER']

const Trigger = styled.button`
  appearance: none;
  border: 1px solid ${(p) => p.theme?.color?.border || '#E6E0D6'};
  background: ${(p) => p.theme?.color?.bgRaised || '#fff'};
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 16px;
  color: ${(p) => p.theme?.color?.ink || '#1F2937'};
  position: relative;
  transition: background 200ms ease-out;
  &:hover {
    background: ${(p) => p.theme?.color?.bgSubtle || '#F4F0E9'};
  }
`

const Container = styled.div`
  position: relative;
`

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 280px;
  background: ${(p) => p.theme?.color?.bgRaised || '#fff'};
  border: 1px solid ${(p) => p.theme?.color?.border || '#E6E0D6'};
  border-radius: 16px;
  box-shadow: ${(p) => p.theme?.shadow?.raised || '0 4px 16px rgba(31,41,55,0.08)'};
  padding: 8px;
  z-index: ${(p) => p.theme?.z?.modal || 60};
`

const SectionLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${(p) => p.theme?.color?.inkMuted || '#6B7280'};
  padding: 8px 12px 4px;
`

const PortalRow = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 12px;
  background: ${(p) => (p.$active ? p.theme?.color?.primarySoft || '#E8EEF6' : 'transparent')};
  border: none;
  cursor: ${(p) => (p.$active ? 'default' : 'pointer')};
  font-family: inherit;
  font-size: 14px;
  text-align: left;
  &:hover {
    background: ${(p) => p.$active ? p.theme?.color?.primarySoft || '#E8EEF6' : p.theme?.color?.bgSubtle || '#F4F0E9'};
  }
`

const RowText = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`

const RowName = styled.span`
  font-weight: 600;
  color: ${(p) => p.$active ? p.theme?.color?.primary || '#1F3A5F' : p.theme?.color?.ink || '#1F2937'};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const ActivePill = styled.span`
  font-size: 10px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 999px;
  background: ${(p) => p.theme?.color?.primarySoft || '#E8EEF6'};
  color: ${(p) => p.theme?.color?.primary || '#1F3A5F'};
  border: 1px solid ${(p) => p.theme?.color?.primarySoft || '#E8EEF6'};
  white-space: nowrap;
`

function isCurrentPortal(portal) {
  if (portal.external) return false
  const path = window.location.pathname
  if (portal.path === '/') {
    return (
      path === '/' ||
      (!path.startsWith('/support-home') &&
        !path.startsWith('/mentor-home') &&
        !path.startsWith('/clinic-home') &&
        !path.startsWith('/ca-home') &&
        !path.startsWith('/kiosk') &&
        !path.startsWith('/workplace') &&
        !path.startsWith('/patient-home'))
    )
  }
  return path === portal.path || path.startsWith(portal.path + '/')
}

export default function PatientPortalSwitcher() {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    function handleOutsideClick(e) {
      if (!ref.current || ref.current.contains(e.target)) return
      setOpen(false)
    }
    if (open) document.addEventListener('mousedown', handleOutsideClick)
    return () => document.removeEventListener('mousedown', handleOutsideClick)
  }, [open])

  const userRoles = getUserRoles()

  const hasNonPatientRole =
    Array.isArray(userRoles) && userRoles.some((r) => !PATIENT_CONSUMER_ROLES.includes(r))

  const availablePortals = hasNonPatientRole
    ? PORTAL_CONFIG.filter((p) => p.roles.some((r) => userRoles.includes(r)))
    : []

  if (!hasNonPatientRole || availablePortals.length === 0) return null

  function handleSwitch(portal) {
    setOpen(false)
    setTimeout(() => {
      if (portal.external) {
        window.open(portal.path, '_blank', 'noopener,noreferrer')
        return
      }
      if (portal.path === '/') {
        sessionStorage.setItem('explicitlyChoseConsumerPortal', 'true')
        sessionStorage.setItem('chosenSomeFlowInThisSession', 'true')
        sessionStorage.removeItem('portalAutoNavigatedToAtFlow')
        localStorage.removeItem('lastChosenFlow')
        window.location.replace('/')
        return
      }
      const flowMap = {
        'clinic-assistant': 'clinic-assistant',
        kiosk: 'kiosk',
        mentor: 'mentor',
        clinic: 'clinic',
        support: 'support',
        business: 'business',
      }
      if (flowMap[portal.id]) {
        localStorage.setItem('lastChosenFlow', flowMap[portal.id])
      }
      navigate(portal.path)
    }, 100)
  }

  return (
    <Container ref={ref}>
      <Trigger
        aria-label="Switch portal"
        aria-haspopup="listbox"
        aria-expanded={open}
        title="Switch portal"
        onClick={() => setOpen((v) => !v)}
      >
        ⇄
      </Trigger>

      {open && (
        <Dropdown role="listbox">
          <SectionLabel>Switch to</SectionLabel>
          {availablePortals.map((portal) => {
            const active = isCurrentPortal(portal)
            return (
              <PortalRow
                key={portal.id}
                $active={active}
                onClick={() => !active && handleSwitch(portal)}
              >
                <RowText>
                  <RowName $active={active}>{portal.name}</RowName>
                </RowText>
                {active && <ActivePill>Current</ActivePill>}
              </PortalRow>
            )
          })}
        </Dropdown>
      )}
    </Container>
  )
}
